import { createFileRoute, useNavigate, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { toast } from 'sonner'
import { BrandMark } from '@/components/atoms'
import { signInWithGoogle, signOut } from '@/lib/firebase'
import { useAuthStore } from '@/stores/auth-store'

export const Route = createFileRoute('/auth')({
  component: AuthPage,
})

function AuthPage() {
  const navigate = useNavigate()
  const [busy, setBusy] = useState(false)
  const [underage, setUnderage] = useState(false)
  const setStatus = useAuthStore((s) => s.setStatus)
  const firebaseUser = useAuthStore((s) => s.firebaseUser)

  async function handleGoogle() {
    if (busy) return
    setBusy(true)
    setStatus('signingIn')
    try {
      await signInWithGoogle()
      toast.success('환영해요')
      // 명시적 사용자 액션 결과로만 이동 — 자동 redirect 안 함.
      navigate({ to: '/projects' })
    } catch (err) {
      const message = err instanceof Error ? err.message : '로그인이 되지 않았어요'
      if (!message.includes('popup-closed-by-user') && !message.includes('cancelled')) {
        toast.error('로그인 실패: ' + message)
      }
      setStatus('signedOut')
    } finally {
      setBusy(false)
    }
  }

  async function handleSignOut() {
    try {
      await signOut()
      toast.info('로그아웃됐어요')
    } catch (err) {
      toast.error('로그아웃 실패: ' + (err instanceof Error ? err.message : ''))
    }
  }

  // 이미 로그인된 상태에서 /auth 접근한 경우 — 자동 이동하지 않고 선택지 제시.
  if (firebaseUser) {
    return (
      <div className="center-stage">
        <div className="auth-card text-center">
          <div className="flex flex-col items-center">
            <BrandMark size={36} />
            <h2 className="mt-3">이미 로그인돼 있어요</h2>
            <div className="auth-sub">
              {firebaseUser.displayName || firebaseUser.email} 으로 접속 중
            </div>
          </div>
          <Link
            to="/onboarding"
            className="mt-6 inline-flex w-full items-center justify-center rounded-xl bg-primary py-3 text-[14px] font-bold text-white hover:bg-primary-hover"
          >
            시작 화면으로 가기
          </Link>
          <button
            onClick={handleSignOut}
            className="mt-3 w-full rounded-xl bg-surface-alt py-3 text-[13px] font-semibold text-text-2 hover:bg-surface hover:text-text"
          >
            다른 계정으로 로그인
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="center-stage">
      <div className="auth-card">
        <div className="flex flex-col items-center text-center">
          <BrandMark size={36} />
          <h2 className="mt-3">바이브타운 시작하기</h2>
          <div className="auth-sub">10대를 위한 AI 작업실</div>
        </div>

        <button className="oauth-btn" onClick={handleGoogle} disabled={busy}>
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          {busy ? '로그인 중…' : 'Google로 계속하기'}
        </button>

        <button className="oauth-btn kakao" disabled>
          <span className="font-extrabold">K</span> 카카오로 계속하기
        </button>
        <div className="text-center text-[11px] text-text-3 mt-1 mb-3">
          카카오 로그인은 곧 열어둘게요
        </div>

        <label className="flex gap-2 items-center text-[12px] text-text-2 mt-4 mb-2 cursor-pointer">
          <input
            type="checkbox"
            checked={underage}
            onChange={(e) => setUnderage(e.target.checked)}
            className="accent-primary"
          />
          만 14세 미만이에요
        </label>

        {underage && (
          <div className="consent-banner">
            <strong>보호자 동의가 필요해요.</strong>
            <br />
            로그인하고 나서 보호자 이메일을 알려주시면 안내 메일을 보내드려요.
            보호자 이메일은 동의 확인용으로만 써요. 마케팅 메일은 보내지 않아요.
          </div>
        )}

        <p className="mt-6 text-center text-[12px] leading-[1.6] text-text-3">
          계속하면 <span className="font-semibold text-text-2">이용약관</span>과{' '}
          <span className="font-semibold text-text-2">개인정보 처리방침</span>에 동의한 것으로 봐요.
        </p>
      </div>
    </div>
  )
}
